import type { Json } from "@/integrations/supabase/types";
import { logPlatformEvent } from "@/lib/platform-event-logger";
import { schemaForEventName, type FinanceBaseEvent } from "@/lib/finance-instrumentation/schemas";
import type { FinanceEventName } from "@/lib/finance-instrumentation/registry";

type EmitFinanceEventOptions = {
  strict?: boolean;
  category?: "system" | "integration";
};

export async function emitFinanceEvent(
  event: FinanceBaseEvent & Record<string, unknown>,
  options?: EmitFinanceEventOptions,
) {
  const schema = schemaForEventName(event.event_name as FinanceEventName);
  const parsed = schema.safeParse(event);

  if (!parsed.success) {
    if (options?.strict) {
      throw new Error(`Invalid finance event payload: ${parsed.error.message}`);
    }
    // Intentionally non-blocking in non-strict mode.
    console.debug("[finance-instrumentation] Invalid event payload", parsed.error.issues);
    return null;
  }

  await logPlatformEvent({
    category: options?.category ?? "system",
    eventType: parsed.data.event_name,
    status: "success",
    userId: parsed.data.user_id ?? null,
    source: parsed.data.source_system,
    metadata: parsed.data as unknown as Json,
  });

  return parsed.data.event_id;
}

type FinanceEventContext = {
  accountId: string;
  userId?: string | null;
  requestId?: string | null;
  sessionId?: string | null;
  environment?: "prod" | "staging" | "dev";
  sourceSystem?: "web_app" | "api" | "worker" | "billing_service" | "edge_function";
  countryCode?: string | null;
};

export function buildFinanceBaseEvent(
  eventName: FinanceEventName,
  context: FinanceEventContext,
  amountMinor: number | null,
  currency = "USD",
): FinanceBaseEvent {
  const now = new Date().toISOString();
  return {
    event_id: crypto.randomUUID(),
    event_name: eventName,
    event_timestamp: now,
    received_at: now,
    schema_version: "1.0.0",
    user_id: context.userId ?? null,
    account_id: context.accountId,
    session_id: context.sessionId ?? null,
    request_id: context.requestId ?? null,
    idempotency_key: null,
    source_system: context.sourceSystem ?? "web_app",
    environment: context.environment ?? "dev",
    currency,
    amount_minor: amountMinor,
    country_code: context.countryCode ?? null,
    is_backfill: false,
  };
}

